import isEmpty from "lodash/isEmpty";
import moment from "moment";

import { BOOKING_CONSTANTS, QUOTA_INFO_ARRAY } from "@constants/Bookings";
import { EventBackgroundType, EventType } from "@enums/component.enum";
import { DateFormats } from "@enums/shared.enum";
import { TempBookingExtendedProps } from "@models/components/booking.model";
import {
  AllowedDuration,
  AllowedDurationsMap,
  PolicyInfo,
  QuotaInfo,
  timeMapObj,
} from "@models/services/booking";

import { generateRandomId, toMinutes } from "./common.utils";

export const finalTimeSlotsForDrawer = (
  timeMaps: Array<Array<timeMapObj>>,
  selectedTime: string,
  selectedDate: string
): Array<timeMapObj> => {
  const flatTimes = timeMaps.flat();
  if (flatTimes.length === 0) return [];

  let startIndex = selectedTime
    ? flatTimes.findIndex(t => t.time === selectedTime)
    : -1;

  if (startIndex === -1) {
    const isToday = moment(selectedDate, DateFormats.YMD).isSame(
      moment(),
      DateFormats.Day
    );

    let nowMinutes = toMinutes(selectedTime);

    if (isToday && isEmpty(selectedTime)) {
      nowMinutes = toMinutes(moment().format(DateFormats.Time));
    }

    startIndex = flatTimes.findIndex(
      t => !t.disabled && toMinutes(t.time) > nowMinutes
    );
  }

  if (startIndex === -1) return [];

  return flatTimes.slice(startIndex, startIndex + 4);
};

export const getTimesAfter = (times: string[], givenTime: string): string[] => {
  if (times.length === 0 || !givenTime) return [];

  const base = toMinutes(givenTime);
  return times.filter(t => toMinutes(t) >= base);
};

export const checkQuotaExceeded = (
  quotaInfo: QuotaInfo
): { isExceeded: boolean; messages: string[] } => {
  const messages: string[] = [];
  let isExceeded = false;

  if (quotaInfo) {
    QUOTA_INFO_ARRAY.forEach(quota => {
      if (quotaInfo[quota]) {
        isExceeded = true;
        messages.push(quotaInfo[quota]?.quotaMessage);
      }
    });
  }

  return { isExceeded, messages };
};

export const validateConsecutiveSlots = (
  startTime: string,
  duration: number,
  availableSlots: string[],
  occupiedSlots: string[],
  widthPerSlot: number = BOOKING_CONSTANTS.WIDTH_PER_SLOT
): boolean => {
  const splitValue = duration / widthPerSlot - 1;
  let timeMoment = moment(startTime, DateFormats.Time);
  const baseDate = moment(timeMoment.format(DateFormats.YMD));

  for (let i = 0; i <= splitValue; i++) {
    const currentTimeStr = timeMoment.format(DateFormats.Time);

    if (
      !availableSlots.includes(currentTimeStr) ||
      occupiedSlots.includes(currentTimeStr) ||
      !moment(baseDate).isSame(moment(timeMoment.format(DateFormats.YMD)))
    ) {
      return false;
    }

    timeMoment = timeMoment.add(widthPerSlot, "minutes");
  }

  return true;
};

export const adjustStartTimeFromTimeMaps = (
  startTime: string,
  flatTimeMaps: string[]
): string | null => {
  if (flatTimeMaps.includes(startTime)) {
    return startTime;
  }

  const timesAfterStart = getTimesAfter(flatTimeMaps, startTime);

  return timesAfterStart.length > 0 ? timesAfterStart[0] : null;
};

export const getDurationConfig = (
  allowedDurations: AllowedDurationsMap,
  startTime: string,
  requestedDuration: number
): {
  config: AllowedDuration;
  actualDuration: number;
  endTime: string;
} | null => {
  const key = `T_${startTime}`;
  const duration = allowedDurations?.[key];

  if (!duration) {
    return null;
  }

  const minutesDiffKey = `T_${requestedDuration}`;
  const durationKeys = Object.keys(duration);
  let durationConfig = duration[minutesDiffKey];
  let actualDuration = requestedDuration;

  // Fallback to first available duration
  if (!durationConfig && durationKeys.length > 0) {
    durationConfig = duration[durationKeys[0]];
    actualDuration = durationConfig?.duration || requestedDuration;
  }

  if (!durationConfig) {
    return null;
  }

  const endTime = moment(startTime, DateFormats.Time)
    .add(actualDuration, "minutes")
    .format(DateFormats.Time);

  return {
    actualDuration,
    config: durationConfig,
    endTime,
  };
};

export const validateAdvancedBooking = (
  selectedDate: string,
  selectedTime: string,
  policyInfo: PolicyInfo,
  t: (key: string, params?: Record<string, string>) => string
): { isValid: boolean; message: string } => {
  const bookingEndAt = moment(
    policyInfo?.bookingEndAt,
    DateFormats.YMDTimeWithSeconds
  );

  const selectedDateTimeMoment = moment(
    `${selectedDate}T${selectedTime}`,
    DateFormats.YMDTime
  );

  if (
    bookingEndAt.isValid() &&
    selectedDateTimeMoment.isAfter(bookingEndAt) &&
    !isEmpty(policyInfo?.bookingAdvanceType) &&
    policyInfo?.bookingAdvanceValue
  ) {
    const message = t("Calendar.advanceBookingMsg", {
      time: subtractBookingAdvanceTime(
        selectedDateTimeMoment,
        policyInfo.bookingAdvanceValue,
        policyInfo.bookingAdvanceType
      ),
    });

    return { isValid: false, message };
  }

  return { isValid: true, message: "" };
};

export const subtractBookingAdvanceTime = (
  targetDateTime: moment.Moment,
  advanceValue: number,
  advanceType: string
): string => {
  const deadline = moment(targetDateTime);

  switch (advanceType?.toLowerCase()) {
    case "minutes":
    case "minute":
      deadline.subtract(advanceValue, "minutes");
      break;
    case "hours":
    case "hour":
      deadline.subtract(advanceValue, "hours");
      break;
    case "days":
    case "day":
      deadline.subtract(advanceValue, "days");
      break;
    case "weeks":
    case "week":
      deadline.subtract(advanceValue, "weeks");
      break;
    case "months":
    case "month":
      deadline.subtract(advanceValue, "months");
      break;
    default:
      deadline.subtract(advanceValue, "hours");
  }

  return deadline.format(DateFormats.YMDTime);
};

export const buildTempEvent = ({
  end,
  eventId = "",
  prevExtendedProps = {},
  resourceId,
  start,
}: {
  start: Date;
  end: Date;
  resourceId: string;
  eventId?: string;
  prevExtendedProps?: Partial<TempBookingExtendedProps>;
}) => {
  const startTime = moment(start).format(DateFormats.Time);
  const endTime = moment(end).format(DateFormats.Time);

  return {
    ...prevExtendedProps,
    className: ["fg-temp-event"],
    display: EventBackgroundType.Block,
    durationEditable: true,
    editable: true,
    end: moment(end).format(DateFormats.YMDTime),
    eventDurationEditable: true,
    eventResizableFromStart: true,
    id: eventId ?? generateRandomId("fg-temp-event"),
    resizableFromStart: true,
    resourceId,
    showDrawer: true,
    showPopup: false,
    start: moment(start).format(DateFormats.YMDTime),
    startEditable: true,
    title: `${startTime} - ${endTime}`,
    type: EventType.TempBooking,
  };
};
